import { forwardRef, memo, useDeferredValue, useMemo } from "react";
import { Search, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtMoney } from "@/lib/api";
import { matchesSku } from "@/lib/pos-utils";
import { FieldError } from "./field-error";

const SkuTile = memo(function SkuTile({ sku, onAdd }) {
  const outOfStock = Number(sku.stock) <= 0;
  return (
    <button
      type="button"
      onClick={outOfStock ? undefined : () => onAdd(sku)}
      disabled={outOfStock}
      aria-disabled={outOfStock}
      title={outOfStock ? "Hết hàng" : undefined}
      aria-label={`Thêm ${sku.name}, giá ${fmtMoney(sku.price)}, tồn ${sku.stock}`}
      className={`text-left rounded-sm p-2.5 border transition-colors ${
        outOfStock
          ? "bg-stone-100 border-stone-200 opacity-60 cursor-not-allowed"
          : "bg-stone-50 border-stone-200 hover:border-emerald-700 hover:bg-emerald-50"
      }`}
      data-testid={`sku-tile-${sku.code}`}
    >
      <div className="text-[10px] text-stone-700 font-mono">{sku.code}</div>
      <div className="text-sm font-semibold leading-tight">{sku.name}</div>
      {(sku.uom || sku.barcode) && (
        <div className="text-[10px] text-stone-700 font-mono truncate">
          {sku.uom ? sku.uom : ""}
          {sku.uom && sku.barcode ? " • " : ""}
          {sku.barcode || ""}
        </div>
      )}
      <div className="flex justify-between items-end mt-1">
        <span className="text-emerald-800 font-mono text-sm font-bold">{fmtMoney(sku.price)}</span>
        <span className={`text-[10px] font-mono ${outOfStock ? "text-red-700 font-semibold" : "text-stone-700"}`}>
          {outOfStock ? "Hết hàng" : `tồn: ${sku.stock}`}
        </span>
      </div>
    </button>
  );
});

export const SkuGrid = forwardRef(function SkuGrid(
  { skus, q, onQueryChange, branchId, branches, onBranchChange, branchError, onAdd, onScannerEnter, loading },
  searchRef
) {
  const deferredQ = useDeferredValue(q);
  const filtered = useMemo(() => {
    if (!skus) return [];
    return skus.filter((s) => matchesSku(s, deferredQ));
  }, [skus, deferredQ]);

  const handleKeyDown = (e) => {
    if (e.key !== "Enter") return;
    const exact = (skus || []).find(
      (s) => s.barcode && String(s.barcode).trim() === q.trim()
    );
    if (exact) {
      e.preventDefault();
      onScannerEnter?.(exact);
    }
  };

  return (
    <div className="lg:col-span-3 bg-white border border-stone-200 rounded-sm">
      <div className="px-4 py-3 border-b border-stone-200 flex gap-2 items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" aria-hidden="true" />
          <Input
            ref={searchRef}
            placeholder="Tìm SKU theo mã/tên/barcode... (F2)"
            value={q}
            onChange={(e) => onQueryChange(e.target.value)}
            onKeyDown={handleKeyDown}
            className="pl-8 h-9 rounded-sm"
            aria-label="Tìm SKU"
            data-testid="pos-search"
          />
        </div>
        <div>
          <Select value={branchId} onValueChange={onBranchChange}>
            <SelectTrigger
              aria-invalid={Boolean(branchError)}
              aria-label="Chi nhánh"
              className={`h-9 rounded-sm w-44 ${branchError ? "border-red-400 focus:ring-red-500" : ""}`}
            >
              <SelectValue placeholder="Chi nhánh" />
            </SelectTrigger>
            <SelectContent>
              {branches.map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError message={branchError} />
        </div>
      </div>
      <div className="p-3 grid grid-cols-2 md:grid-cols-3 gap-2 max-h-[60vh] overflow-y-auto">
        {loading && Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={`sk-${i}`} className="h-20 rounded-sm" data-testid="sku-skeleton" />
        ))}
        {!loading && filtered.map((s) => (
          <SkuTile key={s.id} sku={s} onAdd={onAdd} />
        ))}
        {!loading && filtered.length === 0 && (
          <div className="col-span-full text-center text-stone-700 py-10 text-sm" data-testid="sku-empty">
            <AlertCircle className="w-5 h-5 mx-auto mb-2 text-stone-400" aria-hidden="true" />
            {q ? `Không tìm thấy SKU "${q}"` : "Chưa có SKU"}
            {q && (
              <button type="button" onClick={() => onQueryChange("")} className="block mx-auto mt-2 text-emerald-800 underline text-xs">
                Xóa filter
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
});
